/**
 * Wrapgram Login — global session check.
 * Runs on every page. Updates the header account link to show the customer's name (goes to
 * /pages/account) instead of the default "Log in" link (goes to /pages/login) once a session
 * is confirmed valid.
 *
 * The click handler is attached SYNCHRONOUSLY on page load and always calls preventDefault —
 * navigation only happens once the async /session check resolves. This avoids a real race
 * condition an earlier version had: attaching preventDefault only inside the .then() callback
 * left a window, between page load and the fetch resolving, where a click would fall through to
 * the link's default href (always /pages/login) even for an already-logged-in customer.
 *
 * Also tags the cart with the logged-in customer's id (via Shopify's own /cart/update.js — no
 * new API/token, same endpoint Dawn's cart already runs on). Why this exists: Shopify checkout
 * has no idea our OTP session exists — it only links an order to a customer by matching whatever
 * email/phone gets typed at checkout, which can create a second, unrelated customer record if
 * either is mistyped (or entered for someone else). A cart attribute survives into the order
 * regardless of what's typed in the visible contact fields, giving the phone-sync Worker
 * (wg-integrations/phone-sync) an exact, non-inferred signal for which real account placed
 * the order — see that Worker's own comment for the correction logic this feeds into.
 *
 * Also gates checkout behind a valid session — every order needs a verified phone on file for
 * NimbusPost to work correctly; without this, anyone who never logs in checks out as a pure
 * guest with nothing for phone-sync to correct against (no verified phone exists for them at
 * all). Invisible to anyone already logged in — the gate checks the same cached session promise
 * and lets an already-valid session through with no extra step. Covers button[name="checkout"]
 * (used identically by the cart page, cart drawer, and cart notification popup) by intercepting
 * in the capture phase, then re-dispatching the same click once login is confirmed so Shopify's
 * own cart-form handling runs exactly as it normally would — see the click listener below for
 * why this re-dispatch approach was used instead of calling form.submit() directly (that would
 * lose the clicked button's name=checkout value, which Shopify's cart form relies on).
 * window.wrapgramRequireLogin is exposed for the Buy Now button (wrapgram-product-
 * personalization.js), which doesn't go through a submit button at all — it redirects directly.
 */
(function () {
  var API_BASE = 'https://wrapgram-customer-login.wrapgram.workers.dev';
  var SESSION_KEY = 'wrapgram_session_token';
  // The drawer's own account entry isn't a plain navigate-on-click link any more (see
  // snippets/header-drawer.liquid) — it's an expandable trigger revealing "Log In" or "My
  // Account" + "Log Out", so it's handled separately below instead of through this click-to-
  // navigate loop.
  var LINK_IDS = [
    { link: 'wrapgram-header-account-link', label: 'wrapgram-header-account-label' },
  ];

  function getToken() {
    try { return localStorage.getItem(SESSION_KEY); } catch (e) { return null; }
  }
  function clearToken() {
    try { localStorage.removeItem(SESSION_KEY); } catch (e) {}
  }

  // Resolves to: the customer object (valid session), null (CONFIRMED invalid — a real 401), or
  // the string 'unknown' (couldn't verify at all — network/parse error). Never rejects.
  // Distinguishing the last two matters: a transient blip checking a token that's actually still
  // fine must never be treated the same as an actual logged-out state — that would silently clear
  // a valid cart tag (see tagCart's caller below) or, worse, force an already-logged-in customer
  // back through login at the worst possible moment if it happens to land on the checkout gate.
  // Every consumer below treats a plain truthy check (customer ? ... : ...) as "proceed as logged
  // in" — 'unknown' passes that check same as a real customer object, so on a genuine network
  // error this optimistically lets the customer through wherever they were going; if the token
  // really was invalid, whatever page they land on (account, addresses, checkout) does its own
  // proper /session check and will catch that correctly at that point.
  function checkSession() {
    var token = getToken();
    if (!token) return Promise.resolve(null);
    return fetch(API_BASE + '/session', { headers: { Authorization: 'Bearer ' + token } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data.ok) { clearToken(); return null; }
        return data.customer;
      })
      .catch(function () { return 'unknown'; }); // couldn't verify — not the same as confirmed logged-out
  }

  var CART_TAG_KEY = 'wrapgram_customer_id';
  var lastTaggedId = null; // avoids re-POSTing the same tag on every single page load

  function tagCart(customerId) {
    if (lastTaggedId === customerId) return;
    lastTaggedId = customerId;
    var attributes = {};
    attributes[CART_TAG_KEY] = customerId || ''; // empty string clears it on logout
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).catch(function () {}); // best-effort — a failed tag must never block browsing/checkout
  }

  function init() {
    var sessionCheck = checkSession();

    sessionCheck.then(function (customer) {
      // 'unknown' specifically skips tagging rather than being treated as logged-in here — unlike
      // navigation, tagging with the wrong value has a real cost (see checkSession's own comment),
      // so this one spot deliberately does NOT use the same permissive truthy check as everywhere
      // else: leave whatever tag is already on the cart from a previous, successful check alone.
      if (customer === 'unknown') return;
      tagCart(customer ? customer.id : null);
    });

    LINK_IDS.forEach(function (ids) {
      var link = document.getElementById(ids.link);
      var label = document.getElementById(ids.label);
      if (!link) return;

      // Attached immediately, regardless of whether the session check has resolved yet.
      link.addEventListener('click', function (e) {
        e.preventDefault();
        sessionCheck.then(function (customer) {
          // ?redirect= so a logged-out click here lands back on /pages/account after login,
          // instead of always going home — same pattern as wrapgramRequireLogin and the checkout
          // gate below (this link was the one place that pattern was missed originally).
          window.location.href = customer ? '/pages/account' : '/pages/login?redirect=' + encodeURIComponent('/pages/account');
        });
      });

      sessionCheck.then(function (customer) {
        if (!customer || !label) return;
        label.textContent = customer.firstName ? 'Hi, ' + customer.firstName : 'Account';
      });
    });

    // Drawer account expand panel (snippets/header-drawer.liquid) — same session check and
    // label text as above, but toggling which real destinations show (Log In vs My Account /
    // My Orders / Addresses + Log Out) instead of navigating on click.
    var drawerTrigger = document.getElementById('wrapgram-drawer-account-trigger');
    var drawerPanel = document.getElementById('wrapgram-drawer-account-panel');
    var drawerLabel = document.getElementById('wrapgram-drawer-account-label');
    var drawerHeading = document.getElementById('wrapgram-drawer-account-heading');

    // Not the generic .wrapgram-menu-top__trigger handler (assets/wrapgram-mega-menu.js) — that
    // one targets trigger.nextElementSibling, which here would be the Wishlist link (both sit
    // inside the same .wrapgram-utility-split row, matching the approved design's 50/50 bar),
    // not this panel. Same open/close mechanics (toggle .is-open, same CSS transition) via id
    // references instead.
    if (drawerTrigger && drawerPanel) {
      function toggleDrawerAccountPanel(e) {
        if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        if (e.type === 'keydown') e.preventDefault();
        var isOpen = drawerPanel.classList.contains('is-open');
        drawerPanel.classList.toggle('is-open', !isOpen);
        drawerTrigger.setAttribute('aria-expanded', String(!isOpen));
      }
      drawerTrigger.addEventListener('click', toggleDrawerAccountPanel);
      drawerTrigger.addEventListener('keydown', toggleDrawerAccountPanel);
    }

    if (drawerLabel) {
      sessionCheck.then(function (customer) {
        if (!customer) return; // stays as the default-rendered "Log In" + guest items
        var name = customer.firstName ? 'Hi, ' + customer.firstName : 'Account';
        drawerLabel.textContent = name;
        if (drawerHeading) drawerHeading.textContent = name;
        document.querySelectorAll('[data-wrapgram-account-guest]').forEach(function (el) {
          el.style.display = 'none';
        });
        document.querySelectorAll('[data-wrapgram-account-member]').forEach(function (el) {
          el.style.display = '';
        });
      });
    }
    var drawerLogout = document.getElementById('wrapgram-drawer-account-logout');
    if (drawerLogout) {
      // Same 2 lines as sections/wrapgram-account.liquid's own logout button — not reimplemented
      // differently here. role="button" div (not a real <button>, same MenuDrawer-closing reason
      // as the other drawer triggers), so click alone doesn't cover keyboard activation.
      function logout(e) {
        if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        clearToken();
        window.location.href = '/';
      }
      drawerLogout.addEventListener('click', logout);
      drawerLogout.addEventListener('keydown', logout);
    }

    // Shared by anything that navigates straight to checkout without a submit button (currently
    // just the Buy Now button) — resolves the same cached session check and navigates directly,
    // no return value, callers just invoke it in place of their own redirect. Passes ?redirect=
    // so the login page can send the customer back to checkout afterward instead of home — see
    // wrapgram-login.liquid for the matching read+validate side of this.
    window.wrapgramRequireLogin = function (destinationIfLoggedIn) {
      sessionCheck.then(function (customer) {
        window.location.href = customer ? destinationIfLoggedIn : '/pages/login?redirect=' + encodeURIComponent(destinationIfLoggedIn);
      });
    };

    // Checkout gate — button[name="checkout"] is Shopify's own convention, used identically by
    // the cart page, cart drawer, and cart notification popup, so one delegated listener covers
    // all three. Capture phase: must see the click before Shopify's own cart-form JS does.
    document.addEventListener('click', function (e) {
      var checkoutBtn = e.target.closest('button[name="checkout"]');
      if (!checkoutBtn || checkoutBtn.dataset.wrapgramGateChecked) return; // already passed the gate once — let it through natively
      e.preventDefault();
      e.stopImmediatePropagation();
      sessionCheck.then(function (customer) {
        if (customer) {
          // Re-dispatch the SAME click rather than calling form.submit() directly — a
          // programmatic form.submit() drops the clicked button's name=checkout value, which
          // Shopify's own cart-form handling relies on to know checkout (not just cart update)
          // was requested. The flag stops this second pass from being intercepted again.
          checkoutBtn.dataset.wrapgramGateChecked = 'true';
          checkoutBtn.click();
        } else {
          window.location.href = '/pages/login?redirect=' + encodeURIComponent('/checkout');
        }
      });
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
